/**
 * Post-Mortem / RCA Template Form
 * Root cause analysis with Five Whys methodology
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const PostMortemForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    incident_id: '', incident_title: '', date: new Date().toISOString().split('T')[0], facilitator: '',
    attendees: [''], incident_summary: '', impact_summary: '', duration: '', severity: 'Medium',
    timeline: [{ timestamp: '', action: '', actor: '', notes: '' }],
    root_cause: '', contributing_factors: [''], what_went_well: [''], what_went_wrong: [''], where_we_got_lucky: [''],
    five_whys: ['', '', '', '', ''],
    action_items: [{ category: '', item: '', details: '', action_required: '', owner: '', due_date: '', status: 'Pending' }],
    detection_time: '', time_to_mitigate: '', time_to_resolve: '', customer_impact: '',
    rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    incident_id: 'INC-2025-001',
    incident_title: 'Database Connection Pool Exhaustion',
    date: new Date().toISOString().split('T')[0],
    facilitator: 'John Smith (SRE Lead)',
    attendees: ['John Smith (SRE)', 'Jane Doe (Backend)', 'Mike Wilson (DBA)', 'Sarah Chen (Product)', 'Tech Lead'],
    incident_summary: 'Production database connection pool was exhausted due to a slow query introduced in a recent deployment, causing widespread API failures and service degradation for 75 minutes.',
    impact_summary: '~2,500 users affected. 15% error rate on API requests. Revenue impact estimated at $5,000 in failed transactions.',
    duration: '75 minutes (14:30 - 15:45 UTC)',
    severity: 'High',
    timeline: [
      { timestamp: '14:30', action: 'Alert triggered - High DB connection count', actor: 'PagerDuty', notes: '' },
      { timestamp: '14:35', action: 'IC assigned and investigation started', actor: 'John Smith', notes: '' },
      { timestamp: '14:42', action: 'Root cause identified - slow query', actor: 'Mike Wilson', notes: '' },
      { timestamp: '15:05', action: 'Mitigation applied - killed queries', actor: 'Team', notes: '' },
      { timestamp: '15:45', action: 'Incident resolved', actor: 'John Smith', notes: '' }
    ],
    root_cause: 'A query introduced in deployment v2.4.3 was missing an index on the user_orders table. Under load, this query took 30+ seconds instead of <100ms, holding database connections and eventually exhausting the pool.',
    contributing_factors: [
      'No query performance testing in CI/CD pipeline',
      'Connection pool size not scaled with traffic growth',
      'Alert threshold too high (90% vs recommended 70%)',
      'Code review did not catch missing index'
    ],
    what_went_well: [
      'Alert triggered within 5 minutes of issue start',
      'IC responded quickly and assembled the right team',
      'Clear communication via status page',
      'Runbook for connection pool issues was helpful',
      'Team collaboration was excellent'
    ],
    what_went_wrong: [
      'Slow query made it to production without detection',
      'Initial response focused on wrong service (API Gateway)',
      'No automated rollback triggered',
      'Customer support was not looped in early enough'
    ],
    where_we_got_lucky: [
      'DBA happened to be available despite being off-shift',
      'Issue occurred during business hours when team was available',
      'No data corruption occurred'
    ],
    five_whys: [
      'Why did the API fail? Database connection pool was exhausted.',
      'Why was the pool exhausted? Queries were taking 30+ seconds and holding connections.',
      'Why were queries slow? A critical index was missing on user_orders table.',
      'Why was the index missing? Query was added without performance testing.',
      'Why no performance testing? Our CI/CD pipeline lacks query performance checks.'
    ],
    action_items: [
      { category: 'Prevention', item: 'Add query performance testing to CI', details: 'Use pgbench or similar to detect slow queries before deployment', action_required: 'Implement and document', owner: 'Backend Team', due_date: '2025-02-01', status: 'In Progress' },
      { category: 'Detection', item: 'Lower connection pool alert threshold', details: 'Change from 90% to 70% utilization', action_required: 'Update alerting rules', owner: 'SRE Team', due_date: '2025-01-20', status: 'Completed' },
      { category: 'Mitigation', item: 'Implement connection pool auto-scaling', details: 'Scale pool based on demand', action_required: 'Design and implement', owner: 'Platform Team', due_date: '2025-02-15', status: 'Pending' },
      { category: 'Process', item: 'Add DBA review for queries on large tables', details: 'Require DBA sign-off for queries affecting tables >1M rows', action_required: 'Update PR checklist', owner: 'Tech Lead', due_date: '2025-01-22', status: 'Pending' }
    ],
    detection_time: '5 minutes',
    time_to_mitigate: '35 minutes',
    time_to_resolve: '75 minutes',
    customer_impact: 'High - 2,500 users affected, ~$5,000 revenue impact',
    rai_assignments: [
      { task: 'Implement CI query performance tests', responsible: 'Backend Team', accountable: 'Tech Lead', informed: 'All Engineering', due_date: '2025-02-01', status: 'In Progress' }
    ],
    followups: [
      { item: 'Share post-mortem with full engineering org', owner: 'SRE Lead', due_date: '2025-01-18', priority: 'Medium', status: 'Pending' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Post-Mortem / RCA Draft";
      const payload = {
        name: draftName,
        template_type: "Post-Mortem / RCA",
        template_id: "post-mortem",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "post-mortem", "Post Mortem", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("post-mortem", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/post-mortem`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `PostMortem_${formData.incident_id || 'Document'}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { console.error("Error:", error); alert("Error generating document."); }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Post-Mortem / RCA",
        formData,
        settings: activeSettings || {},
        templateType: "Post-Mortem / RCA",
        templateId: "generate",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const addActionItem = () => {
    setFormData({
      ...formData,
      action_items: [...formData.action_items, { category: '', item: '', details: '', action_required: '', owner: '', due_date: '', status: 'Pending' }]
    });
  };

  const updateActionItem = (index, field, value) => {
    const items = [...formData.action_items];
    items[index] = { ...items[index], [field]: value };
    setFormData({ ...formData, action_items: items });
  };

  const removeActionItem = (index) => {
    setFormData({ ...formData, action_items: formData.action_items.filter((_, i) => i !== index) });
  };

  const severities = ['Critical', 'High', 'Medium', 'Low'];
  const categories = ['Prevention', 'Detection', 'Mitigation', 'Response', 'Process', 'Other'];
  const statuses = ['Pending', 'In Progress', 'Completed', 'Deferred'];

  const renderListField = (field, label, placeholder, bgColor = 'bg-gray-50') => (
    <div className={`p-3 rounded-lg ${bgColor}`}>
      <Label className="font-medium">{label}</Label>
      {formData[field].map((item, idx) => (
        <div key={idx} className="flex gap-2 mt-2">
          <Input value={item} onChange={(e) => {
            const items = [...formData[field]];
            items[idx] = e.target.value;
            setFormData({ ...formData, [field]: items });
          }} placeholder={placeholder} className="text-sm" />
          <Button variant="ghost" size="icon" onClick={() => setFormData({ ...formData, [field]: formData[field].filter((_, i) => i !== idx) })}><Trash2 className="h-4 w-4 text-red-500" /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => setFormData({ ...formData, [field]: [...formData[field], ''] })} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add</Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">Post-Mortem / RCA</h1><p className="text-sm text-gray-500">Root cause analysis with Five Whys</p></div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Post-Mortem Details</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><Label>Incident ID *</Label><Input value={formData.incident_id} onChange={(e) => setFormData({...formData, incident_id: e.target.value})} placeholder="INC-2025-001" /></div>
              <div className="col-span-2"><Label>Incident Title *</Label><Input value={formData.incident_title} onChange={(e) => setFormData({...formData, incident_title: e.target.value})} placeholder="Brief title" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><Label>Facilitator</Label><Input value={formData.facilitator} onChange={(e) => setFormData({...formData, facilitator: e.target.value})} placeholder="Name (Role)" /></div>
              <div><Label>Duration</Label><Input value={formData.duration} onChange={(e) => setFormData({...formData, duration: e.target.value})} placeholder="75 minutes" /></div>
              <div><Label>Severity</Label>
                <Select value={formData.severity} onValueChange={(v) => setFormData({...formData, severity: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{severities.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Customer Impact</Label><Input value={formData.customer_impact} onChange={(e) => setFormData({...formData, customer_impact: e.target.value})} placeholder="High/Medium/Low" /></div>
            </div>

            <div><Label>Incident Summary</Label><Textarea value={formData.incident_summary} onChange={(e) => setFormData({...formData, incident_summary: e.target.value})} placeholder="Brief summary of what happened..." rows={3} /></div>
            <div><Label>Impact Summary</Label><Textarea value={formData.impact_summary} onChange={(e) => setFormData({...formData, impact_summary: e.target.value})} placeholder="Business and user impact..." rows={2} /></div>

            <Separator />

            <div><Label className="font-semibold">Root Cause</Label><Textarea value={formData.root_cause} onChange={(e) => setFormData({...formData, root_cause: e.target.value})} placeholder="The fundamental cause of the incident..." rows={3} /></div>

            <div className="bg-amber-50 p-4 rounded-lg">
              <Label className="font-semibold">Five Whys Analysis</Label>
              <p className="text-xs text-gray-500 mb-2">Keep asking "why" to get to the root cause</p>
              {formData.five_whys.map((why, idx) => (
                <div key={idx} className="mt-2">
                  <Label className="text-xs">Why #{idx + 1}</Label>
                  <Input value={why} onChange={(e) => {
                    const whys = [...formData.five_whys];
                    whys[idx] = e.target.value;
                    setFormData({ ...formData, five_whys: whys });
                  }} placeholder={`Why #${idx + 1}...`} />
                </div>
              ))}
            </div>

            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {renderListField('what_went_well', 'What Went Well ✓', 'Positive observation', 'bg-green-50')}
              {renderListField('what_went_wrong', 'What Went Wrong ✗', 'Issue or failure', 'bg-red-50')}
              {renderListField('where_we_got_lucky', 'Where We Got Lucky 🍀', 'Lucky circumstance', 'bg-blue-50')}
            </div>

            {renderListField('contributing_factors', 'Contributing Factors', 'Factor that contributed to the incident')}

            <Separator />

            <div>
              <Label className="font-semibold text-lg">Action Items</Label>
              <div className="space-y-3 mt-2">
                {formData.action_items.map((item, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg border">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      <div><Label className="text-xs">Category</Label>
                        <Select value={item.category} onValueChange={(v) => updateActionItem(idx, 'category', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2"><Label className="text-xs">Action Item</Label><Input value={item.item} onChange={(e) => updateActionItem(idx, 'item', e.target.value)} placeholder="Action item" className="text-sm" /></div>
                      <div><Label className="text-xs">Owner</Label><Input value={item.owner} onChange={(e) => updateActionItem(idx, 'owner', e.target.value)} placeholder="Owner" className="text-sm" /></div>
                      <div className="flex gap-1 items-end">
                        <div className="flex-1"><Label className="text-xs">Status</Label>
                          <Select value={item.status} onValueChange={(v) => updateActionItem(idx, 'status', v)}>
                            <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>{statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => removeActionItem(idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                      <div className="col-span-2"><Label className="text-xs">Details</Label><Input value={item.details} onChange={(e) => updateActionItem(idx, 'details', e.target.value)} placeholder="Details" className="text-sm" /></div>
                      <div><Label className="text-xs">Due Date</Label><Input type="date" value={item.due_date} onChange={(e) => updateActionItem(idx, 'due_date', e.target.value)} className="text-sm" /></div>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addActionItem} className="mt-3"><Plus className="h-4 w-4 mr-1" /> Add Action Item</Button>
            </div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
              <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download DOCX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PostMortemForm;
